/**
 * Performance Logger Utility
 *
 * This utility provides functions to measure and log performance metrics
 * when the NEXT_PUBLIC_ENABLE_PERFORMANCE_MONITORING environment variable is set to 'true'.
 */

// Check if performance monitoring is enabled from environment variable
// Use functions to safely check environment variables
const isPerformanceMonitoringEnabled = () => {
  try {
    return process.env.NEXT_PUBLIC_ENABLE_PERFORMANCE_MONITORING === 'true';
  } catch (e) {
    return false;
  }
};

const isDebugEnabled = () => {
  try {
    return process.env.NEXT_PUBLIC_DEBUG === 'true';
  } catch (e) {
    return false;
  }
};

// Store start times for different operations
const timers: Record<string, number> = {};

// Initialize metrics storage safely
const initializeMetrics = () => {
  try {
    if (typeof window !== 'undefined') {
      if (!window.performanceMetrics) {
        window.performanceMetrics = {};
      }
      return window.performanceMetrics;
    }
  } catch (e) {
    console.error('Error initializing performance metrics:', e);
  }
  return {};
};

// Fallback for server-side rendering
const metrics: Record<string, { count: number, totalDuration: number, min: number, max: number }> = initializeMetrics();

/**
 * Start timing an operation
 * @param operationName - Unique identifier for the operation
 */
export const startTimer = (operationName: string): void => {
  if (!isPerformanceMonitoringEnabled() && !isDebugEnabled()) return;

  try {
    if (typeof performance !== 'undefined') {
      timers[operationName] = performance.now();
      console.log(`[PERFORMANCE] Started: ${operationName}`);
    }
  } catch (error) {
    console.error(`Error starting timer for ${operationName}:`, error);
  }
};

/**
 * End timing an operation and log the duration
 * @param operationName - Unique identifier for the operation (must match a previous startTimer call)
 * @param additionalInfo - Optional additional information to include in the log
 * @returns The duration in milliseconds, or undefined if no timer was found
 */
export const endTimer = (operationName: string, additionalInfo?: string): number | undefined => {
  if (!isPerformanceMonitoringEnabled() && !isDebugEnabled()) return;

  try {
    if (typeof performance === 'undefined') return;

    if (!timers[operationName]) {
      console.warn(`[PERFORMANCE] No timer found for: ${operationName}`);
      return;
    }

    const endTime = performance.now();
    const duration = endTime - timers[operationName];

    console.log(
      `[PERFORMANCE] ${operationName} completed in ${duration.toFixed(2)}ms${additionalInfo ? ` | ${additionalInfo}` : ''}`
    );

    try {
      // Store metrics for aggregation
      const metricsObj = initializeMetrics();

      if (!metricsObj[operationName]) {
        metricsObj[operationName] = { count: 0, totalDuration: 0, min: Infinity, max: 0 };
      }

      metricsObj[operationName].count++;
      metricsObj[operationName].totalDuration += duration;
      metricsObj[operationName].min = Math.min(metricsObj[operationName].min, duration);
      metricsObj[operationName].max = Math.max(metricsObj[operationName].max, duration);
    } catch (error) {
      console.error(`Error storing metrics for ${operationName}:`, error);
    }

    // Clean up the timer
    delete timers[operationName];

    return duration;
  } catch (error) {
    console.error(`Error ending timer for ${operationName}:`, error);
    return;
  }
};

/**
 * Log a performance metric without using start/end timers
 * @param message - The message to log
 * @param durationMs - The duration in milliseconds (optional)
 * @param data - Additional data to log (optional)
 */
export const logPerformance = (message: string, durationMs?: number, data?: any): void => {
  if (!isPerformanceMonitoringEnabled() && !isDebugEnabled()) return;

  try {
    if (durationMs !== undefined) {
      if (data) {
        console.log(`[PERFORMANCE] ${message}: ${durationMs.toFixed(2)}ms`, data);
      } else {
        console.log(`[PERFORMANCE] ${message}: ${durationMs.toFixed(2)}ms`);
      }
    } else if (data) {
      console.log(`[PERFORMANCE] ${message}`, data);
    } else {
      console.log(`[PERFORMANCE] ${message}`);
    }
  } catch (error) {
    console.error(`Error logging performance for ${message}:`, error);
  }
};

/**
 * Create a wrapped version of a function that logs its execution time
 * @param fn - The function to wrap
 * @param operationName - Name to use in the performance log
 * @returns The wrapped function
 */
export function withPerformanceLogging<T extends (...args: any[]) => any>(
  fn: T,
  operationName: string
): (...args: Parameters<T>) => ReturnType<T> {
  if (!isPerformanceMonitoringEnabled() && !isDebugEnabled()) return fn;

  return (...args: Parameters<T>): ReturnType<T> => {
    try {
      if (typeof performance === 'undefined') return fn(...args);

      const start = performance.now();
      try {
        const result = fn(...args);

        // Handle promises
        if (result instanceof Promise) {
          return result.finally(() => {
            try {
              const end = performance.now();
              const duration = end - start;
              console.log(`[PERFORMANCE] ${operationName} completed in ${duration.toFixed(2)}ms`);

              // Store metrics
              const metricsObj = initializeMetrics();

              if (!metricsObj[operationName]) {
                metricsObj[operationName] = { count: 0, totalDuration: 0, min: Infinity, max: 0 };
              }
              metricsObj[operationName].count++;
              metricsObj[operationName].totalDuration += duration;
              metricsObj[operationName].min = Math.min(metricsObj[operationName].min, duration);
              metricsObj[operationName].max = Math.max(metricsObj[operationName].max, duration);
            } catch (error) {
              console.error(`Error logging performance for ${operationName} (promise):`, error);
            }
          }) as ReturnType<T>;
        }

        // Handle synchronous functions
        const end = performance.now();
        const duration = end - start;
        console.log(`[PERFORMANCE] ${operationName} completed in ${duration.toFixed(2)}ms`);

        try {
          // Store metrics
          const metricsObj = initializeMetrics();

          if (!metricsObj[operationName]) {
            metricsObj[operationName] = { count: 0, totalDuration: 0, min: Infinity, max: 0 };
          }
          metricsObj[operationName].count++;
          metricsObj[operationName].totalDuration += duration;
          metricsObj[operationName].min = Math.min(metricsObj[operationName].min, duration);
          metricsObj[operationName].max = Math.max(metricsObj[operationName].max, duration);
        } catch (error) {
          console.error(`Error storing metrics for ${operationName}:`, error);
        }

        return result;
      } catch (error) {
        try {
          const end = performance.now();
          const duration = end - start;
          console.log(`[PERFORMANCE] ${operationName} failed after ${duration.toFixed(2)}ms`);

          // Store metrics for failures too
          const metricsObj = initializeMetrics();

          if (!metricsObj[`${operationName}:failed`]) {
            metricsObj[`${operationName}:failed`] = { count: 0, totalDuration: 0, min: Infinity, max: 0 };
          }
          metricsObj[`${operationName}:failed`].count++;
          metricsObj[`${operationName}:failed`].totalDuration += duration;
          metricsObj[`${operationName}:failed`].min = Math.min(metricsObj[`${operationName}:failed`].min, duration);
          metricsObj[`${operationName}:failed`].max = Math.max(metricsObj[`${operationName}:failed`].max, duration);
        } catch (loggingError) {
          console.error(`Error logging failure for ${operationName}:`, loggingError);
        }

        throw error;
      }
    } catch (outerError) {
      console.error(`Critical error in performance logging for ${operationName}:`, outerError);
      return fn(...args); // Fall back to original function
    }
  };
}

/**
 * Measure the time taken to fetch data from an API
 * @param url - The URL being fetched
 * @param fetchFn - The fetch function to call
 * @returns The result of the fetch function
 */
export async function measureApiCall<T>(
  url: string,
  fetchFn: () => Promise<T>
): Promise<T> {
  if (!isPerformanceMonitoringEnabled() && !isDebugEnabled()) return fetchFn();

  try {
    if (typeof performance === 'undefined') return fetchFn();

    const start = performance.now();
    const apiName = `API:${url.split('?')[0]}`; // Remove query params for cleaner metrics

    try {
      const result = await fetchFn();
      try {
        const end = performance.now();
        const duration = end - start;
        console.log(`[PERFORMANCE] API call to ${url} completed in ${duration.toFixed(2)}ms`);

        // Store metrics
        const metricsObj = initializeMetrics();

        if (!metricsObj[apiName]) {
          metricsObj[apiName] = { count: 0, totalDuration: 0, min: Infinity, max: 0 };
        }
        metricsObj[apiName].count++;
        metricsObj[apiName].totalDuration += duration;
        metricsObj[apiName].min = Math.min(metricsObj[apiName].min, duration);
        metricsObj[apiName].max = Math.max(metricsObj[apiName].max, duration);
      } catch (loggingError) {
        console.error(`Error logging API call to ${url}:`, loggingError);
      }

      return result;
    } catch (error) {
      try {
        const end = performance.now();
        const duration = end - start;
        console.log(`[PERFORMANCE] API call to ${url} failed after ${duration.toFixed(2)}ms`);

        // Store metrics for failures too
        const metricsObj = initializeMetrics();

        if (!metricsObj[`${apiName}:failed`]) {
          metricsObj[`${apiName}:failed`] = { count: 0, totalDuration: 0, min: Infinity, max: 0 };
        }
        metricsObj[`${apiName}:failed`].count++;
        metricsObj[`${apiName}:failed`].totalDuration += duration;
        metricsObj[`${apiName}:failed`].min = Math.min(metricsObj[`${apiName}:failed`].min, duration);
        metricsObj[`${apiName}:failed`].max = Math.max(metricsObj[`${apiName}:failed`].max, duration);
      } catch (loggingError) {
        console.error(`Error logging failed API call to ${url}:`, loggingError);
      }

      throw error;
    }
  } catch (outerError) {
    console.error(`Critical error in API call performance logging for ${url}:`, outerError);
    return fetchFn(); // Fall back to original function
  }
}

/**
 * Get performance metrics summary
 * @returns Object containing performance metrics
 */
export const getPerformanceMetrics = () => {
  try {
    const result: Record<string, { avg: number, min: number, max: number, count: number }> = {};

    // Use window.performanceMetrics if available, otherwise use local metrics
    const metricsSource = initializeMetrics();

    // No sample metrics - we want to show real data only
    Object.entries(metricsSource).forEach(([id, data]) => {
      if (data && typeof data.totalDuration === 'number' && typeof data.count === 'number') {
        result[id] = {
          avg: data.count > 0 ? data.totalDuration / data.count : 0,
          min: data.min === Infinity ? 0 : data.min,
          max: data.max || 0,
          count: data.count
        };
      }
    });

    return result;
  } catch (error) {
    console.error('Error getting performance metrics:', error);
    return {};
  }
};

/**
 * Log all performance metrics
 * @returns The metrics object
 */
export const logAllPerformanceMetrics = () => {
  if (!isPerformanceMonitoringEnabled() && !isDebugEnabled()) return {};

  try {
    const allMetrics = getPerformanceMetrics();
    //console.log('[PERFORMANCE] Aggregate Metrics:', allMetrics);
    return allMetrics;
  } catch (error) {
    console.error('Error logging all performance metrics:', error);
    return {};
  }
};

export default {
  startTimer,
  endTimer,
  logPerformance,
  withPerformanceLogging,
  measureApiCall,
  getPerformanceMetrics,
  logAllPerformanceMetrics,
  isDebugEnabled: isDebugEnabled(),
  isPerformanceMonitoringEnabled: isPerformanceMonitoringEnabled()
};
